// KokoroTTS Engine - High-quality 82MB TTS model using kokoro-js
import { ITTSEngine, TTSOptions } from "./ITTSEngine";

// All available Kokoro voices from the documentation
const KOKORO_VOICES = {
  // American English - Female
  'af_heart': '🚺❤️',
  'af_alloy': '🚺',
  'af_aoede': '🚺',
  'af_bella': '🚺🔥',
  'af_jessica': '🚺',
  'af_kore': '🚺',
  'af_nicole': '🚺🎧',
  'af_nova': '🚺',
  'af_river': '🚺',
  'af_sarah': '🚺',
  'af_sky': '🚺',
  // American English - Male
  'am_adam': '🚹',
  'am_echo': '🚹',
  'am_eric': '🚹',
  'am_fenrir': '🚹',
  'am_liam': '🚹',
  'am_michael': '🚹',
  'am_onyx': '🚹',
  'am_puck': '🚹',
  'am_santa': '🚹',
  // British English - Female
  'bf_alice': '🚺',
  'bf_emma': '🚺',
  'bf_isabella': '🚺',
  'bf_lily': '🚺',
  // British English - Male
  'bm_daniel': '🚹',
  'bm_fable': '🚹',
  'bm_george': '🚹',
  'bm_lewis': '🚹'
};

export class KokoroEngine implements ITTSEngine {
  name = 'kokoro';
  displayName = 'Kokoro TTS';
  size = '82MB';
  description = 'High-quality, natural';

  private isReadyFlag = false;
  private isInitializingFlag = false;
  private tts: any = null; // KokoroTTS instance
  private audioContext: AudioContext | null = null;
  private currentAudioSource: AudioBufferSourceNode | null = null;
  private downloadProgress: { file: string; progress: number } | null = null;
  private progressCallbacks: Set<() => void> = new Set();

  isReady(): boolean {
    return this.isReadyFlag;
  }

  isInitializing(): boolean {
    return this.isInitializingFlag;
  }

  async initialize(): Promise<void> {
    if (this.isReadyFlag || this.isInitializingFlag) {
      return;
    }

    this.isInitializingFlag = true;
    // Clear any previous progress when starting a new initialization
    this.downloadProgress = null;
    this.notifyProgressUpdate(); // Notify that initialization started

    try {
      console.log('KokoroTTS: Starting initialization...');
      console.log('KokoroTTS: Loading kokoro-js library...');

      // Configure transformers.js environment
      const { env } = await import('@huggingface/transformers');

      // Fix for "not valid JSON" error - disable browser cache
      env.allowLocalModels = false;
      env.allowRemoteModels = true;
      env.useBrowserCache = false; // Critical: prevents JSON parse errors

      console.log('KokoroTTS: Transformers.js environment configured');

      // Dynamically import kokoro-js to avoid build issues
      const { KokoroTTS } = await import('kokoro-js');

      console.log('KokoroTTS: Loading model from ONNX Community...');
      console.log('KokoroTTS: This may take 1-2 minutes on first load...');

      // Use the official v1.0 model
      const model_id = 'onnx-community/Kokoro-82M-v1.0-ONNX';

      console.log('KokoroTTS: Model ID:', model_id);
      console.log('KokoroTTS: Expected config URL:', `https://huggingface.co/${model_id}/resolve/main/config.json`);
      console.log('KokoroTTS: Expected tokenizer URL:', `https://huggingface.co/${model_id}/resolve/main/tokenizer.json`);

      // Track last logged progress per file to reduce verbosity
      const lastLoggedProgress = new Map<string, number>();

      const progressCallback = (progress: any) => {
        // Store progress for UI display - log all progress events for debugging
        console.log('KokoroTTS progress callback:', progress);
        
        if (progress.status === 'progress' && progress.file) {
          const currentProgress = Math.floor(progress.progress);
          this.downloadProgress = { file: progress.file, progress: currentProgress };
          console.log(`KokoroTTS: Stored progress - ${progress.file}: ${currentProgress}%`);
          
          // Notify React components of progress update
          this.notifyProgressUpdate();
          
          const lastProgress = lastLoggedProgress.get(progress.file) || 0;

          // Log at 25%, 50%, 75%, 100% or when file changes
          if (currentProgress === 100 ||
              (currentProgress >= 25 && lastProgress < 25) ||
              (currentProgress >= 50 && lastProgress < 50) ||
              (currentProgress >= 75 && lastProgress < 75)) {
            console.log(`KokoroTTS: ${progress.file} - ${currentProgress}%`);
            lastLoggedProgress.set(progress.file, currentProgress);
          }
        } else if (progress.status === 'done') {
          console.log(`KokoroTTS: ✓ ${progress.file} - complete`);
          // Keep progress until next file starts or initialization completes
          this.notifyProgressUpdate();
        } else if (progress.status === 'initiate') {
          console.log(`KokoroTTS: Loading ${progress.file}...`);
          this.downloadProgress = { file: progress.file, progress: 0 };
          console.log(`KokoroTTS: Stored initiate progress - ${progress.file}: 0%`);
          // Notify React components that download started
          this.notifyProgressUpdate();
        }
      };

      // Check WebGPU availability first
      const hasWebGPU = 'gpu' in navigator;

      if (hasWebGPU) {
        try {
          console.log('KokoroTTS: WebGPU detected, attempting to use GPU acceleration...');
          this.tts = await KokoroTTS.from_pretrained(model_id, {
            dtype: 'fp32', // Use full precision with WebGPU
            device: 'webgpu',
            progress_callback: progressCallback
          });
          console.log('KokoroTTS: ✓ Successfully loaded with WebGPU acceleration');
        } catch (webgpuError) {
          console.warn('KokoroTTS: WebGPU initialization failed, falling back to WASM...', webgpuError);

          // Fallback to WASM with quantized model
          console.log('KokoroTTS: Loading with WASM + q8 quantization...');
          this.tts = await KokoroTTS.from_pretrained(model_id, {
            dtype: 'q8', // Quantized for WASM
            device: 'wasm',
            progress_callback: progressCallback
          });
          console.log('KokoroTTS: ✓ Successfully loaded with WASM backend');
        }
      } else {
        // No WebGPU support, use WASM directly
        console.log('KokoroTTS: WebGPU not available, using WASM + q8 quantization...');
        this.tts = await KokoroTTS.from_pretrained(model_id, {
          dtype: 'q8', // Quantized for WASM
          device: 'wasm',
          progress_callback: progressCallback
        });
        console.log('KokoroTTS: ✓ Successfully loaded with WASM backend');
      }

      console.log('KokoroTTS: Model loaded successfully, initializing audio context...');
      // Initialize Web Audio API
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      this.isReadyFlag = true;
      this.isInitializingFlag = false;
      this.downloadProgress = null; // Clear progress when ready
      this.notifyProgressUpdate(); // Notify that initialization is complete
      console.log('KokoroTTS: Initialization completed successfully');
    } catch (error) {
      console.error('KokoroTTS: Initialization failed:', error);
      console.error('KokoroTTS: Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown'
      });

      // Log additional context to help debug
      if (error instanceof Error && error.message.includes('JSON')) {
        console.error('KokoroTTS: JSON parse error - this usually means HuggingFace files are not accessible');
        console.error('KokoroTTS: Check if the model repository exists and is publicly accessible');
        console.error('KokoroTTS: Model ID:', 'onnx-community/Kokoro-82M-v1.0-ONNX');
      }

      this.isInitializingFlag = false;
      this.isReadyFlag = false;
      // Don't throw - allow graceful degradation to other engines
      console.warn('KokoroTTS: Initialization failed, will not be available');
    }
  }

  async speak(text: string, options?: TTSOptions): Promise<void> {
    if (!this.isReady()) {
      await this.initialize();
    }

    if (!this.tts || !this.audioContext) {
      throw new Error('KokoroTTS not properly initialized');
    }

    this.stop(); // Stop any ongoing speech

    try {
      console.log('KokoroTTS: Generating speech for:', text.substring(0, 50) + '...');

      // Get voice (default to af_heart if not specified)
      const voice = options?.voice || 'af_heart';

      // Generate audio using KokoroTTS as documented
      const audio = await this.tts.generate(text, {
        voice: voice
      });

      console.log('KokoroTTS: Audio generated, converting to Web Audio format...');

      // The audio object from kokoro-js has:
      // - audio.audio: Float32Array of audio samples
      // - audio.sampling_rate: sample rate (usually 24000)
      const audioData = audio.audio;
      const sampleRate = audio.sampling_rate;

      // Create AudioBuffer from the generated audio
      const audioBuffer = this.audioContext.createBuffer(
        1, // mono
        audioData.length,
        sampleRate
      );
      audioBuffer.copyToChannel(audioData, 0);

      // Create source node and configure playback
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.playbackRate.value = options?.speed || 1.0;
      source.connect(this.audioContext.destination);

      console.log('KokoroTTS: Playing audio...');

      // Handle playback events
      return new Promise((resolve, reject) => {
        source.onended = () => {
          this.currentAudioSource = null;
          console.log('KokoroTTS: Speech completed');
          resolve();
        };

        this.currentAudioSource = source;
        source.start();
      });
    } catch (error) {
      console.error(`KokoroTTS speaking failed: "${text}"`, error);
      throw error;
    }
  }

  stop(): void {
    if (this.currentAudioSource) {
      try {
        this.currentAudioSource.stop();
      } catch (e) {
        // Ignore errors if already stopped
      }
      this.currentAudioSource = null;
      console.log("KokoroTTS stopped.");
    }
  }

  getAvailableVoices(): string[] {
    return Object.keys(KOKORO_VOICES);
  }

  getDownloadProgress(): { file: string; progress: number } | null {
    return this.downloadProgress;
  }

  // Subscribe to progress updates
  onProgressUpdate(callback: () => void): () => void {
    this.progressCallbacks.add(callback);
    // Return unsubscribe function
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }

  // Notify all subscribers of progress changes
  private notifyProgressUpdate(): void {
    this.progressCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }
}